// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import { Alert, Box, type BoxProps, Flex, Title } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';

import type { SubmitFormEvent } from 'components/Mantine/Modals/WrappedModal';

import { LoginErrorAlert, type LoginErrorCode } from './LoginErrorAlert';

type Props<T> = {
  title: string;
  children: ReactNode;
  successMessage?: string;
  errorCode?: LoginErrorCode | string;
  onCloseSuccess?: () => void;
  onCloseError?: () => void;
  boxProps?: BoxProps;
} & (
  | {
      form: UseFormReturnType<T>;
      onSubmit: (values: T, event?: SubmitFormEvent) => void;
    }
  | {
      form?: never;
      onSubmit: () => void;
    }
);

export const loginAnimationDuration = 500;

export const AuthFormWrapper = <T,>({
  title,
  form,
  onSubmit,
  children,
  successMessage,
  errorCode,
  onCloseSuccess,
  onCloseError,
  boxProps,
}: Props<T>) => (
  <Box
    {...boxProps}
    style={{
      flex: '1 0 auto',
      maxWidth: 400,
      animation: `fadeIn ${loginAnimationDuration}ms`,
      // TODO: `.style` can now be a function, need to handle it
      // oxlint-disable-next-line typescript/no-misused-spread
      ...boxProps?.style,
    }}
  >
    <Box style={{ minHeight: 50 }}>
      {!!successMessage && (
        <Alert color="green" variant="light-mantine" withCloseButton onClose={onCloseSuccess}>
          {successMessage}
        </Alert>
      )}
      <LoginErrorAlert errorCode={errorCode} onClose={onCloseError} />
    </Box>
    <form
      onSubmit={
        form
          ? form.onSubmit(onSubmit)
          : (e) => {
              onSubmit();
              e.preventDefault();
              e.stopPropagation();
            }
      }
    >
      <Flex direction="column" gap="md" mt="xl" mb={140}>
        <Title fw={400}>{title}</Title>
        {children}
      </Flex>
    </form>
  </Box>
);
